import React, { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import { Box, Card, FormHelperText, Grid, IconButton, MenuItem, Select, Stack, TextField, Tooltip, Typography, FormControl, InputLabel, Button } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import FormBuilder, { FormBuilderRef, FormSchema, FormSchemaField } from '../../../components/formBuilder/formBuilder';
import { COUNTRY_NAMES, CountryCode } from '../../../models/country';
import { Booking, BookingStatusList, LoadingPlace, LoadingPlaceTypes } from '../../../models/booking';
import { useTranslation } from 'react-i18next';
import { Vehicle } from '../../../types';
import { InfoTooltip } from '../../../components/tooltips/infoTooltip';
import { CheckCircle } from '@mui/icons-material';
import DateTimeInput from '../../../components/inputs/DateTimeInput';

export type BookingFormData = Partial<Booking> & {
  id?: number;
  vehicle?: Partial<Vehicle>;
  is_current_booking?: boolean;
  overtimeDetails?: number[];
  goodsType?: string;
}

type Props = {
  formData: BookingFormData;
  actionText?: string | React.ReactNode,
  setFormData: React.Dispatch<React.SetStateAction<BookingFormData>>,
  handleSubmit: () => Promise<void>,
  hideVehicleSelect?: boolean;
}

export type FormRef = {
  validate: () => boolean;
}

export const DEFAULT_LOADING_PLACES: LoadingPlace[] = [
  { loadingType: LoadingPlaceTypes.LOADING, address: { objectType: 'address', city: '', country: '' as CountryCode, geocoded: false, postalCode: '', streetOrPostbox: null, geoCoordinate: undefined as any }, startTime: '', endTime: '', earliestLoadingDate: '', latestLoadingDate: '' },
  { loadingType: LoadingPlaceTypes.UNLOADING, address: { objectType: 'address', city: '', country: '' as CountryCode, geocoded: false, postalCode: '', streetOrPostbox: null, geoCoordinate: undefined as any }, startTime: '', endTime: '', earliestLoadingDate: '', latestLoadingDate: '' },
];

const Form = forwardRef<FormRef, Props>(({
  formData,
  actionText,
  setFormData,
  handleSubmit,
  hideVehicleSelect = false
}, ref) => {
  const { t } = useTranslation();
  const isEdit = !!formData.id;

  const vehicleBuilderRef = useRef<FormBuilderRef>(null);
  const goodsBuilderRef = useRef<FormBuilderRef>(null);
  const editTimesBuilderRef = useRef<FormBuilderRef>(null);
  const additionalBuilderRef = useRef<FormBuilderRef>(null);
  const loadingPlacesRef = useRef<LoadingPlacesSectionRef>(null);

  useImperativeHandle(ref, () => ({
    validate: () => {
      const results: boolean[] = [];
      if (!hideVehicleSelect) results.push(vehicleBuilderRef.current?.validate() ?? false);
      results.push(loadingPlacesRef.current?.validate() ?? true);
      results.push(goodsBuilderRef.current?.validate() ?? true);
      if (isEdit) results.push(editTimesBuilderRef.current?.validate() ?? true);
      results.push(additionalBuilderRef.current?.validate() ?? true);
      return results.every(Boolean);
    }
  }), [hideVehicleSelect, isEdit]);

  const countries = Object.values(CountryCode).map(code => ({ key: `${code} - ${COUNTRY_NAMES[code]}`, value: code }));
  const statusOptions = BookingStatusList.map(status => ({ key: status.key, value: status.value }));

  // Overtime tracking (edit mode only)
  const [loadedEndTime, setLoadedEndTime] = React.useState<Date | null>(null);
  const [endTime, setEndTime] = React.useState<Date | null>(null);
  React.useEffect(() => {
    if (isEdit && formData.endTime) {
      const newEndTime = new Date(formData.endTime);
      setLoadedEndTime(newEndTime);
      if (!endTime) setEndTime(newEndTime);
    }
  }, [formData.id, formData.endTime]); // intentionally omit endTime from deps to avoid loop

  const [max, setMax] = React.useState<number | undefined>(undefined);
  const [overtimeDefaults, setOvertimeDefaults] = React.useState<number[] | undefined>(undefined);
  React.useEffect(() => {
    if (!endTime || !loadedEndTime) { setOvertimeDefaults(undefined); return; }
    const m = (endTime.getTime() - loadedEndTime.getTime()) / 60000;
    if (m <= 0) { setOvertimeDefaults(undefined); return; }
    setMax(m);
    setOvertimeDefaults(m < 60 ? [0, 0, m] : [0, m - 60, m]);
  }, [endTime]);

  const defaultStatus = statusOptions[0]?.value;
  const initializedFormData = {
    ...formData,
    status: formData.status !== undefined ? formData.status : defaultStatus,
    is_current_booking: formData.is_current_booking !== undefined ? formData.is_current_booking : true,
  } as BookingFormData;

  const [vehicleSchema, setVehicleSchema] = React.useState<FormSchema[]>([]);
  const [goodsSchema, setGoodsSchema] = React.useState<FormSchema[]>([]);
  const [editTimesSchema, setEditTimesSchema] = React.useState<FormSchema[]>([]);
  const [additionalSchema, setAdditionalSchema] = React.useState<FormSchema[]>([]);

  React.useEffect(() => {
    if (!hideVehicleSelect) {
      setVehicleSchema([{
        name: 'vehicle',
        label: t('Vehicle'),
        icon: '🚛',
        fields: [
          { name: 'vehicle', type: 'async-select' as const, label: t('Vehicle'), required: true, url: `${process.env.REACT_APP_API_URL}/api/v1/vehicles`, getOptionLabel: (option: Vehicle) => `${option.truckPlate}`, getOptionValue: (option: Vehicle) => option.id, cols: 12 },
        ]
      }]);
    }
  }, [hideVehicleSelect, t]);

  React.useEffect(() => {
    setGoodsSchema([{
      name: 'cargoInfo',
      label: t('Cargo Information'),
      icon: '📦',
      fields: [
        { name: 'goodsType', type: 'text', label: t('Type of Goods'), cols: 12, disable: isEdit },
      ]
    }]);
  }, [isEdit, t]);

  React.useEffect(() => {
    if (!isEdit) { setEditTimesSchema([]); return; }
    setEditTimesSchema([{
      name: 'bookingTimes',
      label: t('Booking Times'),
      icon: '🕐',
      fields: [
        {
          // Change 6: side-by-side (cols 6 each)
          name: 'startTime', type: 'datetime', label: t('Start time'), required: true, cols: 6,
          validationRules: [(value, fd) => {
            if (fd && new Date(value || '') >= new Date((fd as any).endTime || '')) return t('Must be before end time');
          }]
        },
        {
          name: 'endTime', type: 'datetime', label: t('End time'), required: true, cols: 6,
          onChange: (value: string) => setEndTime(new Date(value)),
          validationRules: [(value, fd) => {
            if (fd && new Date(value || '') <= new Date((fd as any).startTime || '')) return t('Must be after start time');
          }]
        },
        ...(overtimeDefaults !== undefined ? [{
          name: 'overtimeDetails',
          type: 'slider' as const,
          label: t('Overtime details'),
          sliderMin: 0,
          sliderMax: max,
          sliderStep: 15,
          sliderMarks: true,
          defaultValue: overtimeDefaults,
          cols: 12,
        } as FormSchemaField] : [])
      ]
    }]);
  }, [isEdit, overtimeDefaults, t]);

  React.useEffect(() => {
    setAdditionalSchema([{
      name: 'additionalInformation',
      label: t('Additional Information'),
      icon: '📝',
      fields: [
        { name: 'externalOfferId', type: 'text', label: t('External offer ID'), cols: 6, disable: isEdit },
        { name: 'status', type: 'select', label: t('Status'), required: true, options: statusOptions, cols: 6 },
        ...(!isEdit ? [{
          name: 'is_current_booking',
          type: 'switch' as const,
          label: t('Search Reference Booking'),
          required: true,
          cols: 6,
          disable: false,
          defaultValue: true,
          render: () => (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {t('Search Reference Booking')}
              <InfoTooltip
                title={t('Search Reference Booking')}
                description={
                  <>
                    {t('This booking serves as the reference point for auto search.')}
                    <br />
                    {t('New offers will be found that start after this booking ends.')}
                    <br />
                    {t('Only one booking per vehicle can be marked as search reference.')}
                  </>
                }
                metaHint={t('Set this booking as the auto search reference point')}
                icon={<CheckCircle />}
              />
            </Box>
          )
        }] : [])
      ]
    }]);
  }, [isEdit, t]);

  // Sync defaults to parent formData
  useEffect(() => {
    if (initializedFormData.status !== formData.status || initializedFormData.is_current_booking !== formData.is_current_booking) {
      setFormData(initializedFormData);
    }
  }, [initializedFormData.status, initializedFormData.is_current_booking, formData.status, formData.is_current_booking, setFormData]);

  const handlePlacesChange = (places: LoadingPlace[]) => {
    setFormData(prev => ({ ...prev, loadingPlaces: places }));
  };

  return (
    <Box>
      {!hideVehicleSelect && vehicleSchema.length > 0 && (
        <FormBuilder
          ref={vehicleBuilderRef}
          formSchema={vehicleSchema}
          formData={initializedFormData}
          setFormData={setFormData}
          handleSubmit={handleSubmit}
          actionText={<></>}
          onSchemaChange={setVehicleSchema}
        />
      )}

      <LoadingPlacesSection
        ref={loadingPlacesRef}
        places={formData.loadingPlaces || []}
        disabled={isEdit}
        onChange={handlePlacesChange}
        countries={countries}
      />

      {/* Change 2: Type of Goods in its own section */}
      {goodsSchema.length > 0 && (
        <FormBuilder
          ref={goodsBuilderRef}
          formSchema={goodsSchema}
          formData={initializedFormData}
          setFormData={setFormData}
          handleSubmit={handleSubmit}
          actionText={<></>}
          onSchemaChange={setGoodsSchema}
        />
      )}

      {isEdit && editTimesSchema.length > 0 && (
        <FormBuilder
          ref={editTimesBuilderRef}
          formSchema={editTimesSchema}
          formData={initializedFormData}
          setFormData={setFormData}
          handleSubmit={handleSubmit}
          actionText={<></>}
          onSchemaChange={setEditTimesSchema}
        />
      )}

      <FormBuilder
        ref={additionalBuilderRef}
        formSchema={additionalSchema}
        formData={initializedFormData}
        setFormData={setFormData}
        handleSubmit={handleSubmit}
        actionText={actionText}
        onSchemaChange={setAdditionalSchema}
      />
    </Box>
  );
});

Form.displayName = 'Form';

export default Form;

// ─── LoadingPlacesSection ─────────────────────────────────────────────────────

interface LoadingPlacesSectionProps {
  places: LoadingPlace[];
  disabled: boolean;
  onChange: (places: LoadingPlace[]) => void;
  countries: { key: string; value: string }[];
}

type LoadingPlacesSectionRef = {
  validate: () => boolean;
}

const SectionCard = (props: any) => (
  <Card sx={{ padding: 2, mb: 2, borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', width: '100%' }} {...props} />
);

// Change 4: derive date format from browser locale via Intl API (no extra imports needed)
const getLocaleDateFormat = (): string => {
  try {
    const parts = new Intl.DateTimeFormat(navigator.language).formatToParts(new Date(2024, 0, 15));
    const map: Record<string, string> = { day: 'DD', month: 'MM', year: 'YYYY' };
    return parts
      .filter(p => ['day', 'month', 'year'].includes(p.type))
      .map(p => map[p.type] ?? '')
      .filter(Boolean)
      .join('/');
  } catch {
    return 'DD/MM/YYYY';
  }
};
const LOCALE_DATE_FORMAT = getLocaleDateFormat();

const LoadingPlacesSection = forwardRef<LoadingPlacesSectionRef, LoadingPlacesSectionProps>(function LoadingPlacesSection({ places, disabled, onChange, countries }, ref) {
  const { t } = useTranslation();
  const [showErrors, setShowErrors] = React.useState(false);

  useImperativeHandle(ref, () => ({
    validate: () => {
      if (disabled) return true;
      const hasLoading = places.some(p => p.loadingType === LoadingPlaceTypes.LOADING);
      const hasUnloading = places.some(p => p.loadingType === LoadingPlaceTypes.UNLOADING);
      const placesValid = places.every(p => !!p.address?.country && (!!p.address?.postalCode || !!p.address?.city) && !!p.earliestLoadingDate);
      const allValid = placesValid && hasLoading && hasUnloading;
      setShowErrors(!allValid);
      return allValid;
    }
  }), [places, disabled]);

  const updatePlace = (index: number, updates: Partial<LoadingPlace>) => {
    onChange(places.map((p, i) => i === index ? { ...p, ...updates } : p));
  };

  const updateAddress = (index: number, field: string, value: string) => {
    onChange(places.map((p, i) => i === index ? { ...p, address: { ...p.address, [field]: value } } : p));
  };

  const removePlace = (index: number) => onChange(places.filter((_, i) => i !== index));

  const addPlace = (type: LoadingPlaceTypes) => onChange([
    ...places,
    { loadingType: type, address: { objectType: 'address', city: '', country: '' as CountryCode, geocoded: false, postalCode: '', streetOrPostbox: null, geoCoordinate: undefined as any }, startTime: '', endTime: '', earliestLoadingDate: '', latestLoadingDate: '' }
  ]);

  const loadingCount = places.filter(p => p.loadingType === LoadingPlaceTypes.LOADING).length;
  const unloadingCount = places.filter(p => p.loadingType === LoadingPlaceTypes.UNLOADING).length;
  const hasLoading = loadingCount > 0;
  const hasUnloading = unloadingCount > 0;

  const inputSx = {
    '& .MuiInputBase-root': { height: '44px', borderRadius: 2, backgroundColor: 'background.inputGrey' },
    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'border.light' },
  };

  let loadingIdx = 0;
  let unloadingIdx = 0;

  return (
    <SectionCard>
      <Stack direction="row" alignItems="center" spacing={1} mb={1}>
        <span>📍</span>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: (theme: any) => theme.typography.fontSizes.body }}>
          {t('Loading & Delivery Places')}
        </Typography>
      </Stack>

      {places.map((place, index) => {
        const isLoading = place.loadingType === LoadingPlaceTypes.LOADING;
        const placeOrdinal = isLoading ? ++loadingIdx : ++unloadingIdx;
        const typeCount = isLoading ? loadingCount : unloadingCount;
        const canRemove = !disabled && typeCount > 1;
        const typeLabel = isLoading ? t('Loading Place') : t('Unloading Place');
        const label = typeCount > 1 ? `${typeLabel} ${placeOrdinal}` : typeLabel;
        const accentColor = isLoading ? 'rgba(99, 102, 241, 0.06)' : 'rgba(245, 158, 11, 0.05)';
        const borderColor = isLoading ? 'rgba(99, 102, 241, 0.2)' : 'rgba(245, 158, 11, 0.2)';

        return (
          <Box
            key={index}
            sx={{ border: `1px solid ${borderColor}`, borderRadius: 2, p: 1.5, mb: 1.5, backgroundColor: accentColor }}
          >
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
              <Typography sx={{ fontWeight: 600, fontSize: (theme: any) => theme.typography.fontSizes.small }}>
                {label}
              </Typography>
              {canRemove && (
                <Tooltip title={t('Remove place')}>
                  <IconButton size="small" onClick={() => removePlace(index)} sx={{ color: 'text.red' }}>
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Stack>

            <Grid container spacing={1}>
              {/* Place Name */}
              <Grid size={12}>
                <TextField
                  fullWidth size="small" label={t('Place Name')} disabled={disabled}
                  value={place.name || ''}
                  onChange={(e) => updatePlace(index, { name: e.target.value })}
                  sx={inputSx}
                />
              </Grid>
              {/* Street / Address */}
              <Grid size={12}>
                <TextField
                  fullWidth size="small" label={t('Street / Address')} disabled={disabled}
                  value={place.address?.streetOrPostbox || ''}
                  onChange={(e) => updateAddress(index, 'streetOrPostbox', e.target.value)}
                  sx={inputSx}
                />
              </Grid>
              {/* Country | Postal Code | City */}
              <Grid size={4}>
                <FormControl fullWidth size="small" required disabled={disabled} sx={inputSx} error={showErrors && !place.address?.country}>
                  <InputLabel required>{t('Country')}</InputLabel>
                  <Select
                    value={place.address?.country || ''}
                    label={`${t('Country')} *`}
                    onChange={(e) => updateAddress(index, 'country', e.target.value)}
                    sx={{ borderRadius: 2 }}
                  >
                    {countries.map(c => <MenuItem key={c.value} value={c.value}>{c.key}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={3}>
                <TextField
                  fullWidth size="small" label={t('Postal code')} disabled={disabled}
                  value={place.address?.postalCode || ''}
                  onChange={(e) => updateAddress(index, 'postalCode', e.target.value)}
                  sx={inputSx}
                  error={showErrors && !place.address?.postalCode && !place.address?.city}
                  helperText={showErrors && !place.address?.postalCode && !place.address?.city ? t('Postal code or city is required') : undefined}
                />
              </Grid>
              <Grid size={5}>
                <TextField
                  fullWidth size="small" label={t('City')} disabled={disabled}
                  value={place.address?.city || ''}
                  onChange={(e) => updateAddress(index, 'city', e.target.value)}
                  sx={inputSx}
                  error={showErrors && !place.address?.postalCode && !place.address?.city}
                />
              </Grid>
              {/* Reference */}
              <Grid size={12}>
                <TextField
                  fullWidth size="small" label={t('Reference')} disabled={disabled}
                  value={place.reference || ''}
                  onChange={(e) => updatePlace(index, { reference: e.target.value })}
                  sx={inputSx}
                />
              </Grid>
              {/* Date | From | To */}
              <Grid size={6}>
                <DateTimeInput
                  type="date"
                  label={`${t('Date')} *`}
                  disabled={disabled}
                  value={place.earliestLoadingDate || null}
                  format={LOCALE_DATE_FORMAT}
                  onChange={(val: any) => {
                    const dateStr = val ? val.format('YYYY-MM-DD') : '';
                    updatePlace(index, { earliestLoadingDate: dateStr, latestLoadingDate: dateStr });
                  }}
                  error={showErrors && !place.earliestLoadingDate}
                  helperText={showErrors && !place.earliestLoadingDate ? t('Date is required') : undefined}
                />
              </Grid>
              <Grid size={3}>
                <DateTimeInput
                  type="time"
                  label={t('From')}
                  disabled={disabled}
                  value={place.startTime ? `2000-01-01T${place.startTime}` : null}
                  onChange={(val: any) => {
                    const timeStr = val ? `${String(val.hour()).padStart(2, '0')}:${String(val.minute()).padStart(2, '0')}` : '';
                    updatePlace(index, { startTime: timeStr });
                  }}
                />
              </Grid>
              <Grid size={3}>
                <DateTimeInput
                  type="time"
                  label={t('To')}
                  disabled={disabled}
                  value={place.endTime ? `2000-01-01T${place.endTime}` : null}
                  onChange={(val: any) => {
                    const timeStr = val ? `${String(val.hour()).padStart(2, '0')}:${String(val.minute()).padStart(2, '0')}` : '';
                    updatePlace(index, { endTime: timeStr });
                  }}
                />
              </Grid>
            </Grid>
          </Box>
        );
      })}

      {/* Change 5: bigger add buttons */}
      {!disabled && (
        <Stack direction="row" spacing={1.5} mt={1}>
          <Button
            size="medium"
            startIcon={<AddIcon />}
            onClick={() => addPlace(LoadingPlaceTypes.LOADING)}
            variant="outlined"
            sx={{ borderRadius: 2, textTransform: 'none', px: 2 }}
          >
            {t('Add Loading')}
          </Button>
          <Button
            size="medium"
            startIcon={<AddIcon />}
            onClick={() => addPlace(LoadingPlaceTypes.UNLOADING)}
            variant="outlined"
            sx={{ borderRadius: 2, textTransform: 'none', px: 2 }}
          >
            {t('Add Unloading')}
          </Button>
        </Stack>
      )}
      {showErrors && (!hasLoading || !hasUnloading) && (
        <FormHelperText error sx={{ mt: 1 }}>
          {t('At least one loading and one unloading place are required')}
        </FormHelperText>
      )}
    </SectionCard>
  );
});

LoadingPlacesSection.displayName = 'LoadingPlacesSection';

